// Debug page to view and fix competition data
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, Button } from '../components';
import {
  getCompetition,
  getPlayersForCompetition,
  updateCompetition,
  createPlayer
} from '../services';
import {
  getCompetitionTokens,
  createPlayerToken
} from '../services/tokenService';
import { storeToken, getTokenForCompetition } from '../utils/localStorage';
import { doc, updateDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { generatePin } from '../utils/crypto';
import { db } from '../firebase/config';

const DebugCompetition = () => {
  const { competitionId } = useParams();
  const navigate = useNavigate();
  const { authUid, user } = useAuth();

  const [competition, setCompetition] = useState(null);
  const [players, setPlayers] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [fixing, setFixing] = useState(false);

  useEffect(() => {
    loadData();
  }, [competitionId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [comp, playersData, tokensData] = await Promise.all([
        getCompetition(competitionId),
        getPlayersForCompetition(competitionId),
        getCompetitionTokens(competitionId)
      ]);

      setCompetition(comp);
      setPlayers(playersData);
      setTokens(tokensData);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleSetAsOrganizer = async (playerId) => {
    if (!window.confirm('Set this player as the organizer?')) return;

    setFixing(true);
    try {
      await updateCompetition(competitionId, { organizerId: playerId });
      setMessage('Organizer updated successfully!');
      await loadData();
    } catch (err) {
      setError(err.message);
    }
    setFixing(false);
  };

  const handleSetOdplayerId = async (playerId) => {
    if (!window.confirm(`Set odplayerId to your current authUid (${authUid})?`)) return;

    setFixing(true);
    try {
      const playerRef = doc(db, 'players', playerId);
      await updateDoc(playerRef, {
        odplayerId: authUid,
        updatedAt: serverTimestamp()
      });
      setMessage('odplayerId updated successfully!');
      await loadData();
    } catch (err) {
      setError(err.message);
    }
    setFixing(false);
  };

  const handleCreateToken = async (playerId) => {
    if (!window.confirm('Create a token for this player linked to your account?')) return;

    setFixing(true);
    try {
      const tokenId = await createPlayerToken(competitionId, playerId, authUid);
      storeToken(competitionId, tokenId);
      setMessage('Token created and stored!');
      await loadData();
    } catch (err) {
      setError(err.message);
    }
    setFixing(false);
  };

  const handleCreatePlayerAndSetOrganizer = async () => {
    const displayName = user?.displayName || 'Organizer';
    if (!window.confirm(`Create player "${displayName}" and set as organizer?`)) return;

    setFixing(true);
    try {
      // Generate a PIN and create the player
      const pin = generatePin(6);
      const player = await createPlayer(competitionId, displayName, pin, null, authUid);

      // Set as organizer
      await updateCompetition(competitionId, { organizerId: player.id });

      // Create and store token
      const tokenId = await createPlayerToken(competitionId, player.id, authUid);
      storeToken(competitionId, tokenId);

      setMessage(`Player created! Your PIN is: ${pin} (save it!)`);
      await loadData();
    } catch (err) {
      setError(err.message);
    }
    setFixing(false);
  };

  const handleDeleteOrphanedTokens = async () => {
    const orphanedTokens = tokens.filter(t => !players.find(p => p.id === t.playerId));
    if (orphanedTokens.length === 0) {
      setMessage('No orphaned tokens to delete');
      return;
    }
    if (!window.confirm(`Delete ${orphanedTokens.length} orphaned token(s)?`)) return;

    setFixing(true);
    try {
      for (const token of orphanedTokens) {
        const tokenRef = doc(db, 'playerTokens', token.id);
        await deleteDoc(tokenRef);
      }
      setMessage(`Deleted ${orphanedTokens.length} orphaned token(s)`);
      await loadData();
    } catch (err) {
      setError(err.message);
    }
    setFixing(false);
  };

  const currentStoredTokenId = getTokenForCompetition(competitionId);
  const orphanedTokens = tokens.filter(t => !players.find(p => p.id === t.playerId));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Debug Competition</h1>
          <Button variant="outline" onClick={() => navigate(-1)}>Back</Button>
        </div>

        {error && (
          <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>
        )}
        {message && (
          <div className="bg-green-100 text-green-700 p-3 rounded mb-4">{message}</div>
        )}

        <Card className="mb-4">
          <h2 className="text-lg font-bold mb-2">Your Auth Info</h2>
          <p><strong>Auth UID:</strong> <code className="bg-gray-100 px-1">{authUid}</code></p>
          <p><strong>Email:</strong> {user?.email || 'N/A'}</p>
          <p><strong>Display Name:</strong> {user?.displayName || 'N/A'}</p>
          <p><strong>Provider:</strong> {user?.providerData?.[0]?.providerId || 'anonymous'}</p>
          <p><strong>Stored Token ID:</strong> <code className="bg-gray-100 px-1">{currentStoredTokenId || 'NONE'}</code></p>
        </Card>

        <Card className="mb-4">
          <h2 className="text-lg font-bold mb-2">Competition</h2>
          <p><strong>Name:</strong> {competition?.name}</p>
          <p><strong>ID:</strong> <code className="bg-gray-100 px-1">{competitionId}</code></p>
          <p><strong>Organizer ID:</strong> <code className="bg-gray-100 px-1">{competition?.organizerId}</code></p>
          <p><strong>League ID:</strong> {competition?.leagueId || 'N/A'}</p>
        </Card>

        <Card className="mb-4">
          <h2 className="text-lg font-bold mb-2">Players ({players.length})</h2>

          {players.length === 0 && (
            <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
              <p className="text-red-700 font-semibold mb-2">No players found!</p>
              <p className="text-sm text-red-600 mb-3">
                This competition has no players. The organizer player may have been deleted.
              </p>
              <Button onClick={handleCreatePlayerAndSetOrganizer} disabled={fixing}>
                Create Player &amp; Set as Organizer
              </Button>
            </div>
          )}

          <div className="space-y-4">
            {players.map(player => {
              const isOrganizer = player.id === competition?.organizerId;
              const hasMatchingOdplayerId = player.odplayerId === authUid;
              const playerToken = tokens.find(t => t.playerId === player.id);

              return (
                <div
                  key={player.id}
                  className={`p-3 rounded border ${isOrganizer ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200'}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold">
                        {player.displayName}
                        {isOrganizer && <span className="ml-2 text-xs bg-yellow-400 px-2 py-0.5 rounded">ORGANIZER</span>}
                        {hasMatchingOdplayerId && <span className="ml-2 text-xs bg-green-400 px-2 py-0.5 rounded">YOUR ACCOUNT</span>}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>ID:</strong> <code className="bg-gray-100 px-1 text-xs">{player.id}</code>
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>odplayerId:</strong> <code className={`px-1 text-xs ${player.odplayerId ? 'bg-gray-100' : 'bg-red-100'}`}>
                          {player.odplayerId || 'NOT SET'}
                        </code>
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Status:</strong> {player.status}
                      </p>
                      {playerToken && (
                        <p className="text-sm text-gray-600">
                          <strong>Token AuthUID:</strong> <code className="bg-gray-100 px-1 text-xs">{playerToken.authUid}</code>
                        </p>
                      )}
                    </div>
                    <div className="space-y-1">
                      {!isOrganizer && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSetAsOrganizer(player.id)}
                          disabled={fixing}
                        >
                          Make Organizer
                        </Button>
                      )}
                      {!hasMatchingOdplayerId && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSetOdplayerId(player.id)}
                          disabled={fixing}
                        >
                          Link to My Account
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCreateToken(player.id)}
                        disabled={fixing}
                      >
                        Create Token
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card>
          <h2 className="text-lg font-bold mb-2">Tokens ({tokens.length})</h2>

          {orphanedTokens.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-3">
              <p className="text-yellow-700 text-sm mb-2">
                {orphanedTokens.length} orphaned token(s) found (linked to deleted players)
              </p>
              <Button size="sm" variant="outline" onClick={handleDeleteOrphanedTokens} disabled={fixing}>
                Delete Orphaned Tokens
              </Button>
            </div>
          )}

          <div className="space-y-2">
            {tokens.map(token => {
              const player = players.find(p => p.id === token.playerId);
              const isYours = token.authUid === authUid;

              return (
                <div
                  key={token.id}
                  className={`p-2 rounded border text-sm ${isYours ? 'border-green-400 bg-green-50' : 'border-gray-200'}`}
                >
                  <p>
                    <strong>Player:</strong> {player?.displayName || 'Unknown'}
                    {isYours && <span className="ml-2 text-xs bg-green-400 px-2 py-0.5 rounded">YOUR TOKEN</span>}
                  </p>
                  <p><strong>Token ID:</strong> <code className="bg-gray-100 px-1 text-xs">{token.id}</code></p>
                  <p><strong>Auth UID:</strong> <code className="bg-gray-100 px-1 text-xs">{token.authUid}</code></p>
                </div>
              );
            })}
          </div>
        </Card>

        <div className="mt-6 text-center">
          <Button onClick={() => navigate(`/competition/${competitionId}`)}>
            Go to Competition
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DebugCompetition;
